import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useIsAuthenticated } from '@azure/msal-react';
import { transformAppsConfig } from '../services/azureStorage';
import appsConfig from '../config.json';
import Navbar from '../components/Navbar';
import '../App.css';
import './showcase.css';

function Showcase() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('All apps');
  const [appsCounter, setAppsCounter] = useState(0);
  const [customersCounter, setCustomersCounter] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  const [statusFilters, setStatusFilters] = useState({ active: true, concept: false });
  const [searchQuery, setSearchQuery] = useState('');
  
  const isAuthenticated = useIsAuthenticated();

  useEffect(() => {
    // Only load data if user is authenticated
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    // Transform local config with Azure Storage URLs
    try {
      const transformedApps = transformAppsConfig(appsConfig);
      setApps(transformedApps);
      setError(null);
    } catch (err) {
      console.error('Failed to load apps config:', err);
      setApps([]);
      setError('Failed to load apps - check config.json and Azure Storage setup');
    }
    setLoading(false);
  }, [isAuthenticated]);

  const filteredApps = apps.filter(app => {
    const categoryMatch = activeFilter === 'All apps' || app.category === activeFilter;
    const status = app.status || 'active';
    const statusMatch = statusFilters[status] === true;
    const searchMatch = searchQuery === '' || app.title.toLowerCase().includes(searchQuery.toLowerCase());
    return categoryMatch && statusMatch && searchMatch;
  });

  const toggleStatusFilter = (status) => {
    setStatusFilters(prev => ({
      ...prev,
      [status]: !prev[status]
    }));
  };

  // Animate counters on mount
  useEffect(() => {
    if (apps.length > 0) {
      const targetApps = Math.floor(apps.length / 10) * 10;
      const targetCustomers = 20;
      const duration = 2000; // 2 seconds
      const steps = 60;
      const stepTime = duration / steps;

      let currentStep = 0;
      const interval = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;
        
        setAppsCounter(Math.floor(targetApps * progress));
        setCustomersCounter(Math.floor(targetCustomers * progress));

        if (currentStep >= steps) {
          setAppsCounter(targetApps);
          setCustomersCounter(targetCustomers);
          clearInterval(interval);
        }
      }, stepTime);

      return () => clearInterval(interval);
    }
  }, [apps.length]);

  return (
    <div className="page-wrapper">
      <Navbar />
      <div className="about-hero">
        <p className="about-hero-eyebrow">Astena App Showcase</p>
      </div>
      <div className="main-wrapper">
        <section className="section_referenties-overview">
          <div className="padding-global">
            <div className="container-large">
              <div className="padding-section-large">

                {/* Filter Buttons */}
                <div className="filters_wrapper">
                  <button 
                    onClick={() => setActiveFilter('All apps')}
                    className={`filter-button ${activeFilter === 'All apps' ? 'active' : ''}`}
                  >
                    <div className="text-block">All apps</div>
                  </button>
                  <button 
                    onClick={() => setActiveFilter('Power Apps')}
                    className={`filter-button ${activeFilter === 'Power Apps' ? 'active' : ''}`}
                  >
                    <div className="text-block">Power Apps</div>
                  </button>
                  <button 
                    onClick={() => setActiveFilter('Native Apps')}
                    className={`filter-button ${activeFilter === 'Native Apps' ? 'active' : ''}`}
                  >
                    <div className="text-block">Native Apps</div>
                  </button>
                  <button 
                    onClick={() => setActiveFilter('Web applications')}
                    className={`filter-button ${activeFilter === 'Web applications' ? 'active' : ''}`}
                  >
                    <div className="text-block">Web applications</div>
                  </button>
                  <div style={{ position: 'relative' }}>
                    <button
                      className="filter-button settings-button"
                      onClick={() => setShowSettings(!showSettings)}
                    >
                      <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 306.256 306.256" xmlSpace="preserve">
                        <g fill="#d8e3e2">
                          <path d="M282.468,166.157v-26.059l21.313-19.243c2.355-2.126,3.118-5.505,1.903-8.437L289.787,74.04
                              c-1.214-2.932-4.139-4.774-7.311-4.62l-28.678,1.465l-18.427-18.427l1.465-28.677c0.162-3.169-1.689-6.098-4.62-7.312
                              L193.837,0.571c-2.933-1.214-6.31-0.452-8.438,1.903l-19.241,21.314h-26.06L120.856,2.474c-2.126-2.355-5.504-3.116-8.437-1.903
                              L74.04,16.468c-2.932,1.214-4.782,4.143-4.62,7.312l1.465,28.677L52.458,70.884L23.781,69.42
                              c-3.166-0.157-6.098,1.688-7.312,4.62L0.572,112.419c-1.214,2.932-0.452,6.311,1.903,8.437l21.313,19.242v26.06L2.475,185.4
                              c-2.355,2.126-3.118,5.505-1.903,8.437l15.897,38.379c1.214,2.932,4.144,4.782,7.312,4.62l28.677-1.465l18.427,18.427
                              l-1.465,28.677c-0.162,3.169,1.689,6.098,4.62,7.312l38.379,15.897c2.933,1.215,6.311,0.453,8.437-1.903l19.243-21.314h26.06
                              l19.241,21.314c1.453,1.609,3.489,2.475,5.569,2.475c0.965,0,1.939-0.186,2.868-0.571l38.379-15.897
                              c2.932-1.214,4.782-4.143,4.62-7.312l-1.465-28.677l18.427-18.427l28.678,1.465c3.175,0.159,6.098-1.689,7.311-4.62
                              l15.897-38.379c1.215-2.932,0.452-6.311-1.903-8.437L282.468,166.157z M277.955,221.586l-26.721-1.365
                              c-2.129-0.107-4.186,0.687-5.686,2.187l-23.141,23.141c-1.5,1.501-2.295,3.566-2.186,5.686l1.365,26.72l-28.599,11.846
                              l-17.929-19.859c-1.423-1.575-3.445-2.474-5.567-2.474h-32.726c-2.122,0-4.145,0.899-5.567,2.474l-17.929,19.859L84.67,277.955
                              l1.365-26.72c0.108-2.12-0.686-4.186-2.187-5.686l-23.141-23.141c-1.5-1.501-3.561-2.295-5.686-2.187l-26.72,1.365
                              l-11.846-28.599l19.859-17.929c1.575-1.422,2.474-3.445,2.474-5.567v-32.726c0-2.122-0.899-4.145-2.474-5.567l-19.859-17.929
                              L28.301,84.67l26.72,1.365c2.123,0.108,4.185-0.686,5.686-2.187l23.141-23.141c1.501-1.5,2.295-3.566,2.187-5.686l-1.365-26.72
                              l28.599-11.846l17.929,19.859c1.422,1.575,3.445,2.474,5.567,2.474h32.726c2.122,0,4.145-0.899,5.567-2.474l17.929-19.859
                              l28.599,11.846l-1.365,26.72c-0.108,2.12,0.686,4.186,2.186,5.686l23.141,23.141c1.5,1.5,3.56,2.297,5.686,2.187l26.721-1.365
                              l11.846,28.599l-19.858,17.929c-1.575,1.422-2.475,3.445-2.475,5.567v32.726c0,2.122,0.899,4.145,2.475,5.567l19.858,17.929
                              L277.955,221.586z"/>
                          <path d="M153.128,71.045c-45.261,0-82.084,36.822-82.084,82.083s36.822,82.084,82.084,82.084
                              c45.261,0,82.083-36.822,82.083-82.084C235.211,107.866,198.389,71.045,153.128,71.045z M153.128,220.212
                              c-36.99,0-67.084-30.094-67.084-67.084s30.094-67.083,67.084-67.083c36.989,0,67.083,30.094,67.083,67.083
                              S190.118,220.212,153.128,220.212z"/>
                          <path d="M153.128,101.184c-28.642,0-51.944,23.302-51.944,51.944s23.302,51.944,51.944,51.944
                              c28.643,0,51.944-23.302,51.944-51.944C205.072,124.486,181.771,101.184,153.128,101.184z M153.128,190.072
                              c-20.371,0-36.944-16.573-36.944-36.944c0-20.371,16.573-36.944,36.944-36.944c20.371,0,36.944,16.573,36.944,36.944
                              C190.072,173.499,173.499,190.072,153.128,190.072z"/>
                        </g>
                      </svg>
                    </button>
                    {showSettings && (
                      <div className="settings-dropdown">
                        <div className="settings-header">App Status</div>
                        <label className="settings-option">
                          <input
                            type="checkbox"
                            checked={statusFilters.active}
                            onChange={() => toggleStatusFilter('active')}
                          />
                          <span>Active Apps</span>
                        </label>
                        <label className="settings-option">
                          <input
                            type="checkbox"
                            checked={statusFilters.concept}
                            onChange={() => toggleStatusFilter('concept')}
                          />
                          <span>Concept Apps</span>
                        </label>
                      </div>
                    )}
                  </div>

                  <div className="search-wrapper">
                    <input
                      type="text"
                      className="search-input"
                      placeholder="Search apps..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                </div>

                {loading && (
                  <div style={{ textAlign: 'center', padding: '2em', color: '#d8e3e2' }}>
                    Loading apps...
                  </div>
                )}
                {error && (
                  <div style={{ textAlign: 'center', padding: '1em', color: '#FF8D40', fontSize: '0.9em' }}>
                    {error}
                  </div>
                )}
                <div className="referenties-collection-list-wrapper w-dyn-list">
                  <div role="list" className="referenties-collection-list w-dyn-items">
                    {filteredApps.length === 0 && !loading && (
                      <div className="no-apps-placeholder">
                        <div className="no-apps-icon">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 115 102">
  <path d="M90.754.307A13.441,13.441,0,1,0,104.2,13.749,13.442,13.442,0,0,0,90.754.307"
        fill="#ff8d40"/>
  
  <path d="M43.519,7.46a43.518,43.518,0,1,0,0,87.037c10.258,0,22.095-3.909,27.133-13.6V93.159H87.037V50.979A43.567,43.567,0,0,0,43.519,7.46m0,70.65a27.132,27.132,0,1,1,27.133-27.13A27.161,27.161,0,0,1,43.519,78.11"
        fill="#d8e3e2"/>
</svg>

                        </div>
                        <h2 className="no-apps-title">No applications found</h2>
                        <p className="no-apps-description">
                          Try adjusting your filter selection or check back later for new applications.
                        </p>
                        <button
                          onClick={() => {
                            setActiveFilter('All apps');
                            setStatusFilters({ active: true, concept: false });
                            setSearchQuery('');
                          }}
                          className="no-apps-button"
                        >
                          View all applications
                        </button>
                      </div>
                    )}
                    {filteredApps.map((app) => (
                      <div
                        key={app.id}
                        data-category={app.category}
                        role="listitem"
                        className="referenties-item w-dyn-item"
                      >
                        <div
                          className="referentie-link"
                          style={{ backgroundImage: `url('${app.image}')` }}
                        >
                          {app.status === 'concept' && (
                            <div className="concept-ribbon">
                              <span>CONCEPT</span>
                            </div>
                          )}
                          <div className="referentie-overlay"></div>
                          <div className="referentie-content">
                            <div className="referentie_title">
                              <h2 className="referentie-titel-item">{app.title}</h2>
                              <div className="arrow-icon w-embed">
                                <svg xmlns="http://www.w3.org/2000/svg" width="95%" height="95%" viewBox="0 0 12.037 11.761">
                                  <g id="_7-Arrow_Up" data-name="7-Arrow Up" transform="translate(0.356 0.25)">
                                    <path id="Path_31" data-name="Path 31" d="M17.279.943H11.343V0h7.072a.471.471,0,0,1,.471.471V8.015h-.943V1.608L8.121,11.16,7.456,10.5Z" transform="translate(-7.456)" fill="#d8e3e2" stroke="currentColor" strokeWidth="0.5"/>
                                  </g>
                                </svg>
                              </div>
                            </div>
                            <div className="referentie-content_container">
                              <div className="referentie-short w-richtext">
                                <p>{app.short}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <Link to={`/app/${app.name}`} className="referentie-link-block w-inline-block"></Link>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default Showcase;
